import { Box, IconButton, MenuItem, Paper, Select, SvgIcon, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { borderRadius, palette, spaces } from '../../../assets/styles/themes/tokens';
import { zIndex } from '../../../constants';
import getMuiIcon from '../../../utils/getMuiIcon';

interface PodcastAudioPlayerProps {
	src?: string;
	durationLabelFallback?: string;
	onPlay?: () => void;
	onPause?: () => void;
	// One-shot: attempt playback once when this becomes true with a src. The parent owns
	// readiness — this component does not gate on audio status.
	autoPlay?: boolean;
	// Called once after the autoplay attempt settles (played or blocked) so the parent
	// can clear the one-shot intent.
	onAutoPlayConsumed?: () => void;
	// One-shot: pause playback when this becomes true (e.g. from the chat card's
	// pause toggle). Consumed even when nothing is playing so the intent can't fire later.
	pauseRequested?: boolean;
	onPauseRequestConsumed?: () => void;
	// Mirrors the internal playing state so external controls (chat card toggle)
	// stay in sync; reports false on unmount.
	onPlaybackStateChange?: (isPlaying: boolean) => void;
}

const PLAYBACK_RATES = [0.5, 1.0, 1.5, 2.0];

// Fixed so the selector does not resize when switching between speed values.
const SPEED_SELECT_WIDTH_PX = 84;

const formatTime = (seconds: number): string => {
	if (!Number.isFinite(seconds) || seconds < 0) {
		return '0:00';
	}
	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const cardStyles = {
	display: 'flex',
	flexDirection: 'column',
	gap: spaces.sm,
	padding: spaces.md,
	width: '100%',
	border: `1px solid ${palette.divider}`,
	borderRadius: borderRadius.md,
	boxSizing: 'border-box',
	background: palette.background.paperElevation2,
};

const topRowStyles = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: spaces.sm,
};

const leftClusterStyles = {
	display: 'flex',
	alignItems: 'center',
	gap: spaces.sm,
};

const rightClusterStyles = {
	display: 'flex',
	alignItems: 'center',
	gap: spaces.sm,
};

const playButtonStyles = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: 40,
	height: 40,
	borderRadius: borderRadius.xs,
	background: palette.primary.main,
	color: palette.primary.contrastText,
	'&:hover': {
		background: palette.primary.dark,
	},
};

const speedSelectStyles = {
	width: SPEED_SELECT_WIDTH_PX,
	'& .MuiSelect-select': {
		paddingTop: spaces.xxs,
		paddingBottom: spaces.xxs,
	},
};

// On small screens the detail view is a drawer at z-index 1401, so the menu
// popover must sit above it or the speed options open behind the drawer.
const speedMenuProps = {
	sx: { zIndex: zIndex.addMenu },
};

const progressRowStyles = {
	display: 'flex',
	alignItems: 'center',
	gap: spaces.sm,
};

const progressTrackStyles = {
	position: 'relative',
	flex: '1 0 0',
	height: 6,
	borderRadius: borderRadius.full,
	background: palette.divider,
	cursor: 'pointer',
};

const PodcastAudioPlayer: React.FC<PodcastAudioPlayerProps> = ({
	src,
	durationLabelFallback = '1:35',
	onPlay,
	onPause,
	autoPlay = false,
	onAutoPlayConsumed,
	pauseRequested = false,
	onPauseRequestConsumed,
	onPlaybackStateChange,
}) => {
	const { t } = useTranslation();
	const audioRef = useRef<HTMLAudioElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [playbackRate, setPlaybackRate] = useState(1.0);

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) {
			return undefined;
		}
		const handleAudioPlay = () => setIsPlaying(true);
		const handleAudioPause = () => setIsPlaying(false);
		const handleAudioEnded = () => setIsPlaying(false);
		// A src swap aborts playback without a 'pause' event; 'emptied' is the reset signal.
		const handleAudioEmptied = () => setIsPlaying(false);
		const handleAudioTimeUpdate = () => setCurrentTime(audio.currentTime);
		const handleAudioLoadedMetadata = () => setDuration(audio.duration);

		audio.addEventListener('play', handleAudioPlay);
		audio.addEventListener('pause', handleAudioPause);
		audio.addEventListener('ended', handleAudioEnded);
		audio.addEventListener('emptied', handleAudioEmptied);
		audio.addEventListener('timeupdate', handleAudioTimeUpdate);
		audio.addEventListener('loadedmetadata', handleAudioLoadedMetadata);

		return () => {
			audio.removeEventListener('play', handleAudioPlay);
			audio.removeEventListener('pause', handleAudioPause);
			audio.removeEventListener('ended', handleAudioEnded);
			audio.removeEventListener('emptied', handleAudioEmptied);
			audio.removeEventListener('timeupdate', handleAudioTimeUpdate);
			audio.removeEventListener('loadedmetadata', handleAudioLoadedMetadata);
		};
	}, []);

	// Autoplay once when opened via the chat-card play button. onAutoPlayConsumed clears the
	// one-shot intent so a later reopen (Details, Meine Inhalte, direct URL) never autoplays.
	const autoPlayedRef = useRef(false);
	useEffect(() => {
		if (!autoPlay) {
			autoPlayedRef.current = false;
			return undefined;
		}
		const audio = audioRef.current;
		if (!audio || !src || autoPlayedRef.current) {
			return undefined;
		}
		autoPlayedRef.current = true;
		let cancelled = false;
		// play() resolves only if playback actually starts; a rejection (autoplay blocked or the
		// load interrupted) must reset isPlaying so the control does not falsely show "Pause".
		audio
			.play()
			.then(() => {
				if (cancelled) return;
				onPlay?.();
				onAutoPlayConsumed?.();
			})
			.catch(() => {
				if (cancelled) return;
				setIsPlaying(false);
				onAutoPlayConsumed?.();
			});
		return () => {
			// Set synchronously on unmount/re-run before the play() microtask resolves, so no
			// callback or state update runs after this instance is gone.
			cancelled = true;
		};
	}, [autoPlay, src, onPlay, onAutoPlayConsumed]);

	useEffect(() => {
		onPlaybackStateChange?.(isPlaying);
	}, [isPlaying, onPlaybackStateChange]);

	useEffect(
		() => () => {
			onPlaybackStateChange?.(false);
		},
		[onPlaybackStateChange]
	);

	useEffect(() => {
		if (!pauseRequested) {
			return;
		}
		const audio = audioRef.current;
		if (audio && src && !audio.paused) {
			audio.pause();
			// The request comes from a user control, so it counts as a user pause for tracking.
			onPause?.();
		}
		onPauseRequestConsumed?.();
	}, [pauseRequested, src, onPause, onPauseRequestConsumed]);

	const handleTogglePlay = useCallback(() => {
		const audio = audioRef.current;
		if (audio && src) {
			// Callbacks fire from the click (not the audio element events) so
			// programmatic pauses (e.g. seek or unmount) are not reported as user actions.
			if (audio.paused) {
				void audio.play();
				onPlay?.();
			} else {
				audio.pause();
				onPause?.();
			}
			return;
		}
		setIsPlaying((prev) => !prev);
	}, [src, onPlay, onPause]);

	const handleSeek = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			const audio = audioRef.current;
			if (!audio || !src || !Number.isFinite(audio.duration)) {
				return;
			}
			const rect = event.currentTarget.getBoundingClientRect();
			const ratio = (event.clientX - rect.left) / rect.width;
			audio.currentTime = Math.max(0, Math.min(1, ratio)) * audio.duration;
		},
		[src]
	);

	const handleRateChange = useCallback((rate: number) => {
		setPlaybackRate(rate);
		const audio = audioRef.current;
		if (audio) {
			audio.playbackRate = rate;
		}
	}, []);

	const hasRealProgress = Boolean(src) && duration > 0;
	const progressRatio = hasRealProgress ? currentTime / duration : 0;
	const timeLabel = hasRealProgress ? formatTime(currentTime) : durationLabelFallback;

	return (
		<Paper elevation={0} sx={cardStyles} data-testid="podcast-audio-player">
			{/* biome-ignore lint/a11y/useMediaCaption: generated podcast audio has no caption track available */}
			<audio ref={audioRef} src={src} preload="metadata" data-testid="podcast-audio-element" />

			<Box sx={topRowStyles}>
				<Box sx={leftClusterStyles}>
					<IconButton
						onClick={handleTogglePlay}
						aria-label={isPlaying ? t('Pause') : t('Play')}
						data-testid="podcast-audio-player-play-button"
						data-playing={String(isPlaying)}
						// Audio metadata is loaded, so any autoplay decision has already settled — lets a
						// negative "did not autoplay" assertion wait past the mount-time attempt.
						data-ready={String(hasRealProgress)}
						sx={playButtonStyles}
					>
						<SvgIcon component={getMuiIcon(isPlaying ? 'pauseBold' : 'play')} aria-hidden="true" />
					</IconButton>
					<Typography variant="body2" sx={{ color: palette.text.primary }}>
						{t('Audio-Player')}
					</Typography>
				</Box>

				<Box sx={rightClusterStyles}>
					<SvgIcon
						component={getMuiIcon('volume')}
						aria-hidden="true"
						sx={{ color: palette.text.secondary }}
					/>
					<Select
						value={playbackRate}
						onChange={(event) => handleRateChange(Number(event.target.value))}
						size="small"
						variant="outlined"
						aria-label={t('Playback speed')}
						data-testid="podcast-audio-player-speed-select"
						sx={speedSelectStyles}
						MenuProps={speedMenuProps}
					>
						{PLAYBACK_RATES.map((rate) => (
							<MenuItem key={rate} value={rate}>
								{`${rate.toFixed(1)}x`}
							</MenuItem>
						))}
					</Select>
				</Box>
			</Box>

			<Box sx={progressRowStyles}>
				<Box
					sx={progressTrackStyles}
					onClick={handleSeek}
					data-testid="podcast-audio-player-progress-track"
				>
					<Box
						sx={{
							position: 'absolute',
							top: 0,
							left: 0,
							height: '100%',
							width: `${Math.max(0, Math.min(1, progressRatio)) * 100}%`,
							borderRadius: borderRadius.full,
							background: palette.primary.main,
						}}
					/>
				</Box>
				<Typography variant="caption" sx={{ color: palette.text.secondary, minWidth: 36 }}>
					{timeLabel}
				</Typography>
			</Box>
		</Paper>
	);
};

export default PodcastAudioPlayer;
